import { memo, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { MiStackedTypography } from "@miplatform/components";
import { parseFieldValue } from "@miplatform/utils";
import { Box, GridLegacy, Paper, Typography } from "@mui/material";
import { useOrderForceCloseoutCardViewModel } from "./useOrderForceCloseoutCardViewModel";

/**
 * A component that displays detailed information about an order in a card format.
 *
 * @param {Object} props.order - The order object containing information to be displayed.
 *
 * @returns {JSX.Element} The rendered order force closeout card.
 */

export const OrderForceCloseoutCard = memo(({ order }) => {
  const { id } = order;
  const { classes } = useStyles();

  const {
    isLoading,
    getForceCompleteActivity,
    forceCompleteActivity,
    getManagerApprovalForceCompleteActivity,
    managerApprovalForceCompleteActivity,
  } = useOrderForceCloseoutCardViewModel({
    id,
  });

  useEffect(() => {
    getForceCompleteActivity();
    getManagerApprovalForceCompleteActivity();
  }, []);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.header}>
        <Typography className={classes.headerText}>Force Closeout</Typography>
      </Box>
      {!isLoading && (
        <GridLegacy container className={classes.middleContent}>
          <GridLegacy item key="ApprovedOn" xs={6}>
            <MiStackedTypography
              title="Approved On"
              description={parseFieldValue(
                managerApprovalForceCompleteActivity?.createdOn ||
                  forceCompleteActivity?.createdOn
              )}
            />
          </GridLegacy>
          <GridLegacy item key="Manager" xs={6}>
            <MiStackedTypography
              title="Approved By"
              description={parseFieldValue(
                managerApprovalForceCompleteActivity?.createdByUser
                  ?.displayName ||
                  forceCompleteActivity?.createdByUser?.displayName
              )} // take user from manager activity to show who APPROVED it, not who performed it
            />
          </GridLegacy>
          <GridLegacy item key="Reason" xs={12}>
            <MiStackedTypography
              title="Reason"
              description={parseFieldValue(
                forceCompleteActivity?.reason ||
                  managerApprovalForceCompleteActivity?.reason
              )} // take reason from base activity, fallback to manager approval activity
            />
          </GridLegacy>
        </GridLegacy>
      )}
    </Paper>
  );
});

const useStyles = makeStyles()((theme) => ({
  paper: {
    position: "relative",
    borderRadius: theme.spacing(1.5),
    minWidth: 288,
    maxWidth: 350,
    border: `1px solid ${theme.palette.warning.main}`,
  },
  header: {
    backgroundColor: theme.palette.warning.main,
    padding: theme.spacing(1, 2, 1, 2),
    display: "flex",
    justifyContent: "space-between",
    maxWidth: "100%",
  },
  headerText: {
    fontSize: theme.fontSize.small,
    color: theme.palette.warning.contrastText,
  },
  topContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  addressText: {
    fontSize: theme.fontSize.medium,
    weight: theme.fontWeight.semiBold,
  },
  fieldText: {
    display: "flex",
    fontSize: theme.fontSize.small,
    weight: theme.fontWeight.regular,
    color: theme.palette.grey.main,
  },
  middleContent: {
    spacing: theme.spacing(2),
    padding: theme.spacing(1, 2),
  },
  bottomContent: {
    padding: theme.spacing(2),
    maxWidth: "100%",
  },
}));
